import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, CheckCircleIcon, UploadIcon, UserIcon, MapPinIcon, ClockIcon, BriefcaseIcon, BuildingIcon, SparklesIcon, RocketIcon, CreditCardIcon, ShieldCheckIcon, TrendingUpIcon, AlertCircleIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
type Step = 'identity' | 'address' | 'availability' | 'employment' | 'company' | 'deposit';
const steps: {
  id: Step;
  title: string;
  icon: any;
  description: string;
}[] = [{
  id: 'identity',
  title: 'Identitate',
  icon: UserIcon,
  description: 'Informații personale'
}, {
  id: 'address',
  title: 'Adresă',
  icon: MapPinIcon,
  description: 'Locație și domiciliu'
}, {
  id: 'availability',
  title: 'Disponibilitate',
  icon: ClockIcon,
  description: 'Program de lucru'
}, {
  id: 'employment',
  title: 'Situație profesională',
  icon: BriefcaseIcon,
  description: 'Status actual'
}, {
  id: 'company',
  title: 'Firmă',
  icon: BuildingIcon,
  description: 'Detalii companie (opțional)'
}, {
  id: 'deposit',
  title: 'Depozit',
  icon: RocketIcon,
  description: 'Plată finalizare'
}];
export function Onboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    updateProfile
  } = useAuth();
  const email = (location.state as any)?.email || '';
  const [currentStep, setCurrentStep] = useState<Step>('identity');
  const [formData, setFormData] = useState({
    photo: '',
    fullName: '',
    phone: '',
    email: email,
    dateOfBirth: '',
    city: '',
    address: '',
    locality: '',
    availability: 'full-time' as 'full-time' | 'part-time',
    hoursPerWeek: '',
    employmentStatus: '' as 'has-job' | 'in-notice' | 'no-job',
    noticeEndDate: '',
    hasCompany: false,
    companyName: '',
    cui: '',
    caenCode: '6831'
  });
  const currentStepIndex = steps.findIndex(s => s.id === currentStep);
  const progress = (currentStepIndex + 1) / steps.length * 100;
  const updateField = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        updateField('photo', reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const canGoNext = () => {
    switch (currentStep) {
      case 'identity':
        return formData.fullName && formData.phone && formData.dateOfBirth;
      case 'address':
        return formData.city && formData.address && formData.locality;
      case 'availability':
        return formData.availability === 'full-time' || formData.availability === 'part-time' && formData.hoursPerWeek;
      case 'employment':
        return formData.employmentStatus && (formData.employmentStatus !== 'in-notice' || formData.noticeEndDate);
      case 'company':
        return true;
      case 'deposit':
        return true;
      default:
        return false;
    }
  };
  const handleNext = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      setCurrentStep(steps[nextIndex].id);
    }
  };
  const handleBack = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex].id);
    }
  };
  const handleFinish = () => {
    updateProfile({
      fullName: formData.fullName,
      phone: formData.phone,
      email: formData.email,
      dateOfBirth: formData.dateOfBirth,
      photo: formData.photo,
      city: formData.city,
      address: formData.address,
      locality: formData.locality,
      availability: formData.availability,
      hoursPerWeek: formData.hoursPerWeek ? parseInt(formData.hoursPerWeek) : undefined,
      employmentStatus: formData.employmentStatus,
      noticeEndDate: formData.noticeEndDate,
      hasCompany: formData.hasCompany,
      companyName: formData.companyName,
      cui: formData.cui,
      caenCode: formData.caenCode
    });
    // Redirect to profile page after onboarding completion
    navigate('/profile');
  };
  const currentStepData = steps[currentStepIndex];
  const StepIcon = currentStepData.icon;
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50" data-id="element-8579">
      {/* Hero Header - Mobile Optimized */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#132C53] to-[#1e4278]" data-id="element-8580">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" data-id="element-8581" />
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-8582"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-id="element-8583">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-center" data-id="element-8584">
            <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            delay: 0.2,
            type: 'spring',
            stiffness: 200
          }} className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 mb-4 sm:mb-6 shadow-2xl" data-id="element-8585">
              <RocketIcon className="w-8 h-8 sm:w-10 sm:h-10 text-white" data-id="element-8586" />
            </motion.div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3 sm:mb-4 tracking-tight px-4" data-id="element-8587">
              On-boarding CLC
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-300 max-w-2xl mx-auto px-4" data-id="element-8588">
              Completează profilul pentru a activa contul și a debloca toate
              funcționalitățile
            </p>
          </motion.div>
        </div>

        {/* Decorative bottom wave */}
        <div className="absolute bottom-0 left-0 right-0" data-id="element-8589">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-8 sm:h-12" data-id="element-8590">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="rgb(248 250 252)" data-id="element-8591" />
          </svg>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 sm:-mt-8 pb-12 sm:pb-16" data-id="element-8592">
        {/* Progress Card - Mobile Optimized */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-8 shadow-2xl border border-gray-200 mb-6 sm:mb-8" data-id="element-8593">
          {/* Progress Bar */}
          <div className="mb-6 sm:mb-8" data-id="element-8594">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2 sm:mb-3" data-id="element-8595">
              <span className="font-semibold text-xs sm:text-sm" data-id="element-8596">
                Progres completare
              </span>
              <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent" data-id="element-8597">
                {Math.round(progress)}%
              </span>
            </div>
            <div className="bg-gray-200 rounded-full h-2 sm:h-3 overflow-hidden" data-id="element-8598">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${progress}%`
            }} transition={{
              duration: 0.5
            }} className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 h-full rounded-full" data-id="element-8599" />
            </div>
          </div>

          {/* Step Indicators - Mobile: 3 cols, Tablet: 6 cols */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-2" data-id="element-8600">
            {steps.map((step, index) => {
            const isActive = step.id === currentStep;
            const isCompleted = index < currentStepIndex;
            const Icon = step.icon;
            return <motion.div key={step.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4 + index * 0.1
            }} className="relative" data-id="element-8601">
                  <div className={`rounded-xl sm:rounded-2xl p-3 sm:p-4 transition-all ${isCompleted ? 'bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-lg' : isActive ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-xl ring-4 ring-blue-200' : 'bg-gray-100 text-gray-400'}`} data-id="element-8602">
                    <div className="flex flex-col items-center text-center" data-id="element-8603">
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center mb-1 sm:mb-2 ${isCompleted || isActive ? 'bg-white/20' : 'bg-gray-200'}`} data-id="element-8604">
                        {isCompleted ? <CheckCircleIcon className="w-4 h-4 sm:w-6 sm:h-6" data-id="element-8605" /> : <Icon className="w-4 h-4 sm:w-6 sm:h-6" data-id="element-8606" />}
                      </div>
                      <span className="text-[10px] sm:text-xs font-bold leading-tight" data-id="element-8607">
                        {step.title}
                      </span>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </motion.div>

        {/* Form Content Card - Mobile Optimized */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-gray-200 overflow-hidden" data-id="element-8608">
          {/* Step Header */}
          <div className="bg-gradient-to-r from-slate-50 to-blue-50 px-5 sm:px-8 py-5 sm:py-6 border-b-2 border-gray-200" data-id="element-8609">
            <div className="flex items-center space-x-3 sm:space-x-4" data-id="element-8610">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg flex-shrink-0" data-id="element-8611">
                <StepIcon className="w-6 h-6 sm:w-7 sm:h-7 text-white" data-id="element-8612" />
              </div>
              <div data-id="element-8613">
                <h2 className="text-xl sm:text-2xl font-bold text-[#132C53]" data-id="element-8614">
                  {currentStepData.title}
                </h2>
                <p className="text-gray-600 text-sm sm:text-base" data-id="element-8615">
                  {currentStepData.description}
                </p>
              </div>
            </div>
          </div>

          {/* Form Fields */}
          <div className="p-5 sm:p-8" data-id="element-8616">
            <AnimatePresence mode="wait" data-id="element-8617">
              {currentStep === 'identity' && <motion.div key="identity" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.3
            }} className="space-y-5 sm:space-y-6" data-id="element-8618">
                  {/* Photo Upload */}
                  <div className="flex flex-col items-center mb-6 sm:mb-8" data-id="element-8619">
                    <div className="relative group" data-id="element-8620">
                      {formData.photo ? <img src={formData.photo} alt="Profile" className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl object-cover shadow-xl" data-id="element-8621" /> : <div className="w-28 h-28 sm:w-32 sm:h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center shadow-lg" data-id="element-8622">
                          <UserIcon className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" data-id="element-8623" />
                        </div>}
                      <label className="absolute bottom-0 right-0 bg-gradient-to-br from-blue-500 to-indigo-600 text-white p-2.5 sm:p-3 rounded-xl cursor-pointer hover:shadow-xl transition-all group-hover:scale-110" data-id="element-8624">
                        <UploadIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-8625" />
                        <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" data-id="element-8626" />
                      </label>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-500 mt-2 sm:mt-3" data-id="element-8627">
                      Poză de profil (opțional)
                    </p>
                  </div>

                  <div data-id="element-8628">
                    <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-8629">
                      Nume complet *
                    </label>
                    <input type="text" value={formData.fullName} onChange={e => updateField('fullName', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base" placeholder="Popescu Ion" data-id="element-8630" />
                  </div>

                  <div data-id="element-8631">
                    <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-8632">
                      Număr de telefon *
                    </label>
                    <input type="tel" value={formData.phone} onChange={e => updateField('phone', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base" placeholder="+40 7XX XXX XXX" data-id="element-8633" />
                  </div>

                  <div data-id="element-8634">
                    <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-8635">
                      Email
                    </label>
                    <input type="email" value={formData.email} disabled className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-500 text-base" data-id="element-8636" />
                  </div>

                  <div data-id="element-8637">
                    <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-8638">
                      Data nașterii *
                    </label>
                    <input type="date" value={formData.dateOfBirth} onChange={e => updateField('dateOfBirth', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base" data-id="element-8639" />
                  </div>
                </motion.div>}

              {currentStep === 'address' && <motion.div key="address" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.3
            }} className="space-y-5 sm:space-y-6" data-id="element-8640">
                  <div data-id="element-8641">
                    <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-8642">
                      Oraș *
                    </label>
                    <input type="text" value={formData.city} onChange={e => updateField('city', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base" placeholder="București" data-id="element-8643" />
                  </div>

                  <div data-id="element-8644">
                    <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-8645">
                      Adresă de domiciliu *
                    </label>
                    <input type="text" value={formData.address} onChange={e => updateField('address', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base" placeholder="Strada Exemplu, Nr. 1, Bloc A, Ap. 1" data-id="element-8646" />
                  </div>

                  <div data-id="element-8647">
                    <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-8648">
                      Localitate/Sector *
                    </label>
                    <select value={formData.locality} onChange={e => updateField('locality', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base" data-id="element-8649">
                      <option value="" data-id="element-8650">Selectează...</option>
                      <option value="sector-1" data-id="element-8651">Sector 1</option>
                      <option value="sector-2" data-id="element-8652">Sector 2</option>
                      <option value="sector-3" data-id="element-8653">Sector 3</option>
                      <option value="sector-4" data-id="element-8654">Sector 4</option>
                      <option value="sector-5" data-id="element-8655">Sector 5</option>
                      <option value="sector-6" data-id="element-8656">Sector 6</option>
                      <option value="ilfov" data-id="element-8657">Ilfov</option>
                      <option value="alte" data-id="element-8658">Alte</option>
                    </select>
                  </div>
                </motion.div>}

              {currentStep === 'availability' && <motion.div key="availability" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.3
            }} className="space-y-4 sm:space-y-6" data-id="element-8659">
                  <div className="space-y-3 sm:space-y-4" data-id="element-8660">
                    <label className="flex items-center p-5 sm:p-6 border-2 border-gray-300 rounded-xl sm:rounded-2xl cursor-pointer hover:border-blue-500 hover:shadow-lg transition-all group" data-id="element-8661">
                      <input type="radio" name="availability" value="full-time" checked={formData.availability === 'full-time'} onChange={e => updateField('availability', e.target.value)} className="w-5 h-5 text-blue-600 flex-shrink-0" data-id="element-8662" />
                      <div className="ml-3 sm:ml-4" data-id="element-8663">
                        <span className="text-base sm:text-lg font-bold text-gray-900 group-hover:text-blue-600" data-id="element-8664">
                          Full time
                        </span>
                        <p className="text-xs sm:text-sm text-gray-500" data-id="element-8665">
                          Program complet de lucru
                        </p>
                      </div>
                    </label>

                    <label className="flex items-center p-5 sm:p-6 border-2 border-gray-300 rounded-xl sm:rounded-2xl cursor-pointer hover:border-blue-500 hover:shadow-lg transition-all group" data-id="element-8666">
                      <input type="radio" name="availability" value="part-time" checked={formData.availability === 'part-time'} onChange={e => updateField('availability', e.target.value)} className="w-5 h-5 text-blue-600 flex-shrink-0" data-id="element-8667" />
                      <div className="ml-3 sm:ml-4" data-id="element-8668">
                        <span className="text-base sm:text-lg font-bold text-gray-900 group-hover:text-blue-600" data-id="element-8669">
                          Part time
                        </span>
                        <p className="text-xs sm:text-sm text-gray-500" data-id="element-8670">
                          Program flexibil de lucru
                        </p>
                      </div>
                    </label>
                  </div>

                  {formData.availability === 'part-time' && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} data-id="element-8671">
                      <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-8672">
                        Număr de ore / săptămână *
                      </label>
                      <input type="number" value={formData.hoursPerWeek} onChange={e => updateField('hoursPerWeek', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base" placeholder="20" min="1" max="40" data-id="element-8673" />
                      <p className="mt-2 text-xs sm:text-sm text-gray-500 flex items-center" data-id="element-8674">
                        <SparklesIcon className="w-4 h-4 mr-1 flex-shrink-0" data-id="element-8675" />
                        Specificarea orelor ne ajută să îți alocăm volumul
                        corect de muncă
                      </p>
                    </motion.div>}
                </motion.div>}

              {currentStep === 'employment' && <motion.div key="employment" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.3
            }} className="space-y-4 sm:space-y-6" data-id="element-8676">
                  <div className="space-y-3 sm:space-y-4" data-id="element-8677">
                    <label className="flex items-center p-5 sm:p-6 border-2 border-gray-300 rounded-xl sm:rounded-2xl cursor-pointer hover:border-blue-500 hover:shadow-lg transition-all group" data-id="element-8678">
                      <input type="radio" name="employment" value="has-job" checked={formData.employmentStatus === 'has-job'} onChange={e => updateField('employmentStatus', e.target.value)} className="w-5 h-5 text-blue-600 flex-shrink-0" data-id="element-8679" />
                      <div className="ml-3 sm:ml-4" data-id="element-8680">
                        <span className="text-base sm:text-lg font-bold text-gray-900 group-hover:text-blue-600" data-id="element-8681">
                          Lucrez la alt job
                        </span>
                        <p className="text-xs sm:text-sm text-gray-500" data-id="element-8682">
                          Am un job activ în prezent
                        </p>
                      </div>
                    </label>

                    <label className="flex items-center p-5 sm:p-6 border-2 border-gray-300 rounded-xl sm:rounded-2xl cursor-pointer hover:border-blue-500 hover:shadow-lg transition-all group" data-id="element-8683">
                      <input type="radio" name="employment" value="in-notice" checked={formData.employmentStatus === 'in-notice'} onChange={e => updateField('employmentStatus', e.target.value)} className="w-5 h-5 text-blue-600 flex-shrink-0" data-id="element-8684" />
                      <div className="ml-3 sm:ml-4" data-id="element-8685">
                        <span className="text-base sm:text-lg font-bold text-gray-900 group-hover:text-blue-600" data-id="element-8686">
                          Sunt în preaviz
                        </span>
                        <p className="text-xs sm:text-sm text-gray-500" data-id="element-8687">
                          Perioadă de tranziție între joburi
                        </p>
                      </div>
                    </label>

                    <label className="flex items-center p-5 sm:p-6 border-2 border-gray-300 rounded-xl sm:rounded-2xl cursor-pointer hover:border-blue-500 hover:shadow-lg transition-all group" data-id="element-8688">
                      <input type="radio" name="employment" value="no-job" checked={formData.employmentStatus === 'no-job'} onChange={e => updateField('employmentStatus', e.target.value)} className="w-5 h-5 text-blue-600 flex-shrink-0" data-id="element-8689" />
                      <div className="ml-3 sm:ml-4" data-id="element-8690">
                        <span className="text-base sm:text-lg font-bold text-gray-900 group-hover:text-blue-600" data-id="element-8691">
                          Nu am alt job
                        </span>
                        <p className="text-xs sm:text-sm text-gray-500" data-id="element-8692">
                          Disponibil complet pentru CLC
                        </p>
                      </div>
                    </label>
                  </div>

                  {formData.employmentStatus === 'in-notice' && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} data-id="element-8693">
                      <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-8694">
                        Data final preaviz *
                      </label>
                      <input type="date" value={formData.noticeEndDate} onChange={e => updateField('noticeEndDate', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base" data-id="element-8695" />
                    </motion.div>}
                </motion.div>}

              {currentStep === 'company' && <motion.div key="company" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.3
            }} className="space-y-4 sm:space-y-6" data-id="element-8696">
                  <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6" data-id="element-8697">
                    <label className="flex items-center p-5 sm:p-6 border-2 border-gray-300 rounded-xl sm:rounded-2xl cursor-pointer hover:border-blue-500 hover:shadow-lg transition-all group" data-id="element-8698">
                      <input type="radio" name="hasCompany" checked={formData.hasCompany} onChange={() => updateField('hasCompany', true)} className="w-5 h-5 text-blue-600 flex-shrink-0" data-id="element-8699" />
                      <div className="ml-3 sm:ml-4" data-id="element-8700">
                        <span className="text-base sm:text-lg font-bold text-gray-900 group-hover:text-blue-600" data-id="element-8701">
                          Dețin firmă
                        </span>
                        <p className="text-xs sm:text-sm text-gray-500" data-id="element-8702">
                          Am o companie înregistrată
                        </p>
                      </div>
                    </label>

                    <label className="flex items-center p-5 sm:p-6 border-2 border-gray-300 rounded-xl sm:rounded-2xl cursor-pointer hover:border-blue-500 hover:shadow-lg transition-all group" data-id="element-8703">
                      <input type="radio" name="hasCompany" checked={!formData.hasCompany} onChange={() => updateField('hasCompany', false)} className="w-5 h-5 text-blue-600 flex-shrink-0" data-id="element-8704" />
                      <div className="ml-3 sm:ml-4" data-id="element-8705">
                        <span className="text-base sm:text-lg font-bold text-gray-900 group-hover:text-blue-600" data-id="element-8706">
                          Nu dețin firmă
                        </span>
                        <p className="text-xs sm:text-sm text-gray-500" data-id="element-8707">
                          Voi lucra ca persoană fizică
                        </p>
                      </div>
                    </label>
                  </div>

                  {formData.hasCompany && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="space-y-5 sm:space-y-6" data-id="element-8708">
                      <div data-id="element-8709">
                        <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-8710">
                          Denumire firmă (opțional)
                        </label>
                        <input type="text" value={formData.companyName} onChange={e => updateField('companyName', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base" placeholder="SC Exemplu SRL" data-id="element-8711" />
                      </div>

                      <div data-id="element-8712">
                        <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-8713">
                          CUI (opțional)
                        </label>
                        <input type="text" value={formData.cui} onChange={e => updateField('cui', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base" placeholder="RO12345678" data-id="element-8714" />
                      </div>

                      <div data-id="element-8715">
                        <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-8716">
                          Cod CAEN curent
                        </label>
                        <input type="text" value={formData.caenCode} onChange={e => updateField('caenCode', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base" data-id="element-8717" />
                        <p className="mt-2 text-xs sm:text-sm text-gray-500 flex items-center" data-id="element-8718">
                          <SparklesIcon className="w-4 h-4 mr-1 flex-shrink-0" data-id="element-8719" />
                          Cod pentru intermediere imobiliară: 6831
                        </p>
                      </div>
                    </motion.div>}
                </motion.div>}

              {currentStep === 'deposit' && <motion.div key="deposit" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.3
            }} className="space-y-4 sm:space-y-6" data-id="element-8720">
                  {/* Deposit Info Card */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-blue-200" data-id="element-8721">
                    <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-4 mb-4 sm:mb-6" data-id="element-8722">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg flex-shrink-0" data-id="element-8723">
                        <CreditCardIcon className="w-7 h-7 sm:w-8 sm:h-8 text-white" data-id="element-8724" />
                      </div>
                      <div data-id="element-8725">
                        <h3 className="text-xl sm:text-2xl font-bold text-[#132C53] mb-2" data-id="element-8726">
                          Depozit de Securitate
                        </h3>
                        <p className="text-sm sm:text-base text-gray-700 leading-relaxed" data-id="element-8727">
                          Pentru a finaliza procesul de on-boarding și a activa
                          contul, este necesar un depozit de securitate de{' '}
                          <span className="font-bold text-blue-600" data-id="element-8728">300€</span>.
                        </p>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl p-5 sm:p-6 mb-4 sm:mb-6" data-id="element-8729">
                      <div className="flex items-center justify-between mb-4" data-id="element-8730">
                        <span className="text-base sm:text-lg font-semibold text-gray-700" data-id="element-8731">
                          Sumă depozit:
                        </span>
                        <span className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent" data-id="element-8732">
                          300€
                        </span>
                      </div>
                      <div className="h-px bg-gray-200 my-4" data-id="element-8733"></div>
                      <p className="text-xs sm:text-sm text-gray-600" data-id="element-8734">
                        Plata se efectuează securizat prin card bancar
                      </p>
                    </div>

                    {/* Benefits */}
                    <div className="space-y-3" data-id="element-8735">
                      <div className="flex items-start space-x-3" data-id="element-8736">
                        <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-8737">
                          <CheckCircleIcon className="w-4 h-4 text-white" data-id="element-8738" />
                        </div>
                        <div data-id="element-8739">
                          <p className="font-semibold text-gray-900 text-sm sm:text-base" data-id="element-8740">
                            Returnare dublă la prima vânzare
                          </p>
                          <p className="text-xs sm:text-sm text-gray-600" data-id="element-8741">
                            Primești înapoi{' '}
                            <span className="font-bold text-emerald-600" data-id="element-8742">
                              600€
                            </span>{' '}
                            la prima ta tranzacție de vânzare
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3" data-id="element-8743">
                        <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-8744">
                          <TrendingUpIcon className="w-4 h-4 text-white" data-id="element-8745" />
                        </div>
                        <div data-id="element-8746">
                          <p className="font-semibold text-gray-900 text-sm sm:text-base" data-id="element-8747">
                            Bonus suplimentar
                          </p>
                          <p className="text-xs sm:text-sm text-gray-600" data-id="element-8748">
                            600€ se adaugă la comisionul tău din prima
                            tranzacție de vânzare
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3" data-id="element-8749">
                        <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-8750">
                          <ShieldCheckIcon className="w-4 h-4 text-white" data-id="element-8751" />
                        </div>
                        <div data-id="element-8752">
                          <p className="font-semibold text-gray-900 text-sm sm:text-base" data-id="element-8753">
                            Plată securizată
                          </p>
                          <p className="text-xs sm:text-sm text-gray-600" data-id="element-8754">
                            Tranzacție protejată prin procesare bancară
                            securizată
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Important Notice */}
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl sm:rounded-2xl p-5 sm:p-6 border-2 border-amber-200" data-id="element-8755">
                    <div className="flex items-start space-x-3" data-id="element-8756">
                      <AlertCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-8757" />
                      <div data-id="element-8758">
                        <h4 className="font-bold text-amber-900 mb-2 text-sm sm:text-base" data-id="element-8759">
                          Informații importante
                        </h4>
                        <ul className="space-y-2 text-xs sm:text-sm text-amber-800" data-id="element-8760">
                          <li className="flex items-start" data-id="element-8761">
                            <span className="mr-2" data-id="element-8762">•</span>
                            <span data-id="element-8763">
                              Depozitul este complet rambursabil și se dublează
                              la prima vânzare
                            </span>
                          </li>
                          <li className="flex items-start" data-id="element-8764">
                            <span className="mr-2" data-id="element-8765">•</span>
                            <span data-id="element-8766">
                              Plata se procesează instant și securizat prin
                              Stripe
                            </span>
                          </li>
                          <li className="flex items-start" data-id="element-8767">
                            <span className="mr-2" data-id="element-8768">•</span>
                            <span data-id="element-8769">
                              După plată, contul tău va fi activat automat
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Example Calculation */}
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl sm:rounded-2xl p-5 sm:p-6 border-2 border-emerald-200" data-id="element-8770">
                    <h4 className="font-bold text-emerald-900 mb-4 flex items-center text-sm sm:text-base" data-id="element-8771">
                      <SparklesIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" data-id="element-8772" />
                      Exemplu calcul prima vânzare
                    </h4>
                    <div className="space-y-3" data-id="element-8773">
                      <div className="flex justify-between items-center text-sm sm:text-base" data-id="element-8774">
                        <span className="text-gray-700" data-id="element-8775">
                          Comision vânzare (exemplu):
                        </span>
                        <span className="font-bold text-gray-900" data-id="element-8776">3.500€</span>
                      </div>
                      <div className="flex justify-between items-center text-sm sm:text-base" data-id="element-8777">
                        <span className="text-gray-700" data-id="element-8778">
                          Bonus depozit returnat dublu:
                        </span>
                        <span className="font-bold text-emerald-600" data-id="element-8779">
                          + 600€
                        </span>
                      </div>
                      <div className="h-px bg-emerald-300" data-id="element-8780"></div>
                      <div className="flex justify-between items-center" data-id="element-8781">
                        <span className="text-base sm:text-lg font-bold text-gray-900" data-id="element-8782">
                          Total încasat:
                        </span>
                        <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent" data-id="element-8783">
                          4.100€
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Navigation Buttons - Mobile Optimized */}
          <div className="bg-gradient-to-r from-slate-50 to-blue-50 px-5 sm:px-8 py-5 sm:py-6 border-t-2 border-gray-200" data-id="element-8784">
            <div className="flex items-center justify-between gap-3" data-id="element-8785">
              <button onClick={handleBack} disabled={currentStepIndex === 0} className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-3 border-2 border-gray-300 rounded-xl font-bold text-gray-700 hover:bg-gray-50 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base min-h-[44px]" data-id="element-8786">
                <ArrowLeftIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-8787" />
                <span className="hidden sm:inline" data-id="element-8788">Înapoi</span>
              </button>

              {currentStepIndex < steps.length - 1 ? <button onClick={handleNext} disabled={!canGoNext()} className="flex items-center justify-center space-x-2 px-5 sm:px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-bold hover:shadow-2xl transition-all disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 text-sm sm:text-base min-h-[44px] flex-1 sm:flex-initial" data-id="element-8789">
                  <span data-id="element-8790">Continuă</span>
                  <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-8791" />
                </button> : <button onClick={handleFinish} disabled={!canGoNext()} className="flex items-center justify-center space-x-2 px-5 sm:px-8 py-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl font-bold hover:shadow-2xl transition-all disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 text-sm sm:text-base min-h-[44px] flex-1 sm:flex-initial" data-id="element-8792">
                  <span className="hidden sm:inline" data-id="element-8793">Finalizează contul</span>
                  <span className="sm:hidden" data-id="element-8794">Finalizează</span>
                  <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-8795" />
                </button>}
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}